import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import PixelSpinner from '../components/common/PixelSpinner';
import Layout from '../components/Layout';
import { TabbedInterface } from '../components/disclosure';
import BackButton from '../components/common/BackButton';
import { useAuth } from '../context/AuthContext';
import scannerService from '../services/scannerService';
import scannerApi from '../services/scannerApi';
import './Profile.css';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString();
};

const getScanTimestamp = (scan) =>
  scan?.created_at ||
  scan?.createdAt ||
  scan?.started_at ||
  scan?.startedAt ||
  scan?.scan_started_at ||
  scan?.scanStartedAt ||
  scan?.start_time ||
  scan?.startTime ||
  scan?.completed_at ||
  scan?.completedAt ||
  scan?.finished_at ||
  scan?.finishedAt ||
  scan?.updated_at ||
  scan?.modified_at ||
  scan?.updatedAt ||
  null;

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDisplayDate = (dateKey) => {
  if (!dateKey) return '';
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  return parsed.toLocaleDateString();
};

const startOfWeek = (date) => {
  const clone = new Date(date);
  const mondayOffset = (clone.getDay() + 6) % 7;
  clone.setDate(clone.getDate() - mondayOffset);
  return clone;
};

const toStatusLabel = (status) => String(status || 'unknown').replace(/_/g, ' ').toUpperCase();
const scoreScanRecord = (scan) => {
  let score = 0;
  if (getScanTimestamp(scan)) score += 5;
  if (scan?.target_url) score += 2;
  if (scan?.status) score += 1;
  if (scan?.updated_at || scan?.updatedAt) score += 1;
  return score;
};
const normalizeScan = (scan) => ({
  ...scan,
  scan_id: scan?.scan_id || scan?.id,
  target_url: scan?.target_url || scan?.url || scan?.target,
  status: scan?.status,
  created_at:
    scan?.created_at ||
    scan?.createdAt ||
    scan?.started_at ||
    scan?.startedAt ||
    scan?.updated_at ||
    scan?.updatedAt,
  started_at:
    scan?.started_at ||
    scan?.startedAt ||
    scan?.scan_started_at ||
    scan?.scanStartedAt ||
    scan?.start_time ||
    scan?.startTime,
  updated_at:
    scan?.updated_at ||
    scan?.updatedAt ||
    scan?.modified_at ||
    scan?.modifiedAt ||
    scan?.completed_at ||
    scan?.completedAt,
});

const Profile = () => {
  const {
    user,
    updateProfile,
    changePassword,
    setPassword: setAccountPassword,
    resendEmailVerification,
    loading: authLoading,
    isAuthenticated,
    scans: authScans,
  } = useAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [name, setName] = useState(user?.name || user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [socialNewPassword, setSocialNewPassword] = useState('');
  const [socialConfirmPassword, setSocialConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showSocialPassword, setShowSocialPassword] = useState(false);
  const [showSocialConfirmPassword, setShowSocialConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [loadingScans, setLoadingScans] = useState(true);
  const [scanHistory, setScanHistory] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [selectedScanDate, setSelectedScanDate] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setName(user?.name || user?.full_name || '');
    setEmail(user?.email || '');
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setScanHistory([]);
      setRecentScans([]);
      setLoadingScans(false);
      return;
    }

    let cancelled = false;
    const loadRecentScans = async () => {
      try {
        setLoadingScans(true);

        const baselineScans = Array.isArray(authScans) ? authScans.map(normalizeScan) : [];
        // Keep Home and Profile consistent: same primary source first.
        const [homePrimaryResponse, managerResponse] = await Promise.allSettled([
          scannerService.listScans(0, 100),
          scannerApi.getUserScans({ limit: 400 }),
        ]);

        const homePrimaryScans =
          homePrimaryResponse.status === 'fulfilled'
            ? (homePrimaryResponse.value?.scans || [])
            : [];

        const managerRaw =
          managerResponse.status === 'fulfilled'
            ? (Array.isArray(managerResponse.value) ? managerResponse.value : managerResponse.value?.scans || [])
            : [];

        const managerScans = managerRaw.map(normalizeScan);
        const homePrimaryNormalized = homePrimaryScans.map(normalizeScan);

        const merged = [...homePrimaryNormalized, ...managerScans, ...baselineScans];
        const dedupedById = new Map();
        merged.forEach((scan) => {
          const id = scan?.scan_id || scan?.id;
          if (id) {
            const existing = dedupedById.get(id);
            if (!existing || scoreScanRecord(scan) > scoreScanRecord(existing)) {
              dedupedById.set(id, scan);
            }
            return;
          }
          dedupedById.set(`anon-${dedupedById.size}`, scan);
        });

        const scans = Array.from(dedupedById.values()).sort((a, b) => {
          const aTs = new Date(getScanTimestamp(a) || 0).getTime();
          const bTs = new Date(getScanTimestamp(b) || 0).getTime();
          return bTs - aTs;
        });

        if (!cancelled) {
          setScanHistory(scans);
          setRecentScans(scans.slice(0, 5));
        }
      } catch (loadError) {
        console.error('Failed to load recent scans:', loadError);
      } finally {
        if (!cancelled) setLoadingScans(false);
      }
    };

    loadRecentScans();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  const accountAgeDays = useMemo(() => {
    if (!user?.created_at) return 0;
    const created = new Date(user.created_at);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [user?.created_at]);

  const emailVerified = Boolean(user?.is_verified || user?.email_verified);
  const passwordEnabled = user?.preferences?.password_enabled !== false;
  const failedAuthAttempts = Number.isFinite(Number(user?.failed_login_attempts)) ? Number(user?.failed_login_attempts) : 0;
  const lastPasswordChange = user?.preferences?.last_password_change_at || null;
  const heatmap = useMemo(() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);

    const start = new Date(end);
    start.setDate(start.getDate() - 364);

    const byDate = new Map();
    scanHistory.forEach((scan) => {
      const ts = getScanTimestamp(scan);
      if (!ts) return;
      const parsed = new Date(ts);
      if (Number.isNaN(parsed.getTime())) return;
      parsed.setHours(0, 0, 0, 0);
      if (parsed < start || parsed > end) return;
      const key = toDateKey(parsed);
      byDate.set(key, (byDate.get(key) || 0) + 1);
    });

    const counts = Array.from(byDate.values());
    const maxCount = Math.max(...counts, 0);
    const nonZeroCounts = counts.filter((count) => count > 0).sort((a, b) => a - b);
    const quantile = (q) => {
      if (!nonZeroCounts.length) return 0;
      const position = Math.floor((nonZeroCounts.length - 1) * q);
      return nonZeroCounts[position];
    };
    const gridStart = startOfWeek(start);
    const days = [];

    for (let current = new Date(gridStart); current <= end; current.setDate(current.getDate() + 1)) {
      const day = new Date(current);
      day.setHours(0, 0, 0, 0);
      const inRange = day >= start && day <= end;
      const count = inRange ? (byDate.get(toDateKey(day)) || 0) : 0;
      days.push({ date: day, count, inRange });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    let previousMonth = '';
    const monthLabels = weeks.map((week) => {
      const firstInRange = week.find((day) => day.inRange);
      if (!firstInRange) return '';
      const month = firstInRange.date.toLocaleString('default', { month: 'short' });
      if (month === previousMonth) return '';
      previousMonth = month;
      return month;
    });

    const totalScans = counts.reduce((sum, count) => sum + count, 0);
    const activeDays = nonZeroCounts.length;
    return {
      weeks,
      monthLabels,
      maxCount,
      totalScans,
      activeDays,
      thresholds: [quantile(0.25), quantile(0.5), quantile(0.75), quantile(0.95)],
    };
  }, [scanHistory]);

  const getIntensityClass = (count) => {
    if (!count || !heatmap.maxCount) return 'heatmap-cell--0';
    const [q1, q2, q3, q4] = heatmap.thresholds;
    if (count <= q1) return 'heatmap-cell--1';
    if (count <= q2) return 'heatmap-cell--2';
    if (count <= q3) return 'heatmap-cell--3';
    if (count <= q4) return 'heatmap-cell--4';
    return 'heatmap-cell--4';
  };
  const scansForSelectedDate = useMemo(() => {
    if (!selectedScanDate) return [];
    return scanHistory.filter((scan) => {
      const ts = getScanTimestamp(scan);
      if (!ts) return false;
      const parsed = new Date(ts);
      if (Number.isNaN(parsed.getTime())) return false;
      return toDateKey(parsed) === selectedScanDate;
    });
  }, [scanHistory, selectedScanDate]);

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await updateProfile({ full_name: name?.trim() || '' });
      if (result?.success) {
        setSuccess('Profile details updated.');
      } else {
        setError(result?.error || 'Unable to update profile.');
      }
    } catch (updateError) {
      setError(typeof updateError === 'string' ? updateError : updateError?.message || 'Unable to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation don't match.");
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result?.success) {
        setSuccess('Password updated successfully.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result?.error || 'Unable to change password.');
      }
    } catch (passwordError) {
      setError(typeof passwordError === 'string' ? passwordError : passwordError?.message || 'Unable to change password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (event) => {
    event.preventDefault();
    if (socialNewPassword !== socialConfirmPassword) {
      setError("Password and confirmation don't match.");
      return;
    }

    if (socialNewPassword.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }

    if (!/[A-Z]/.test(socialNewPassword) || !/[a-z]/.test(socialNewPassword) || !/\d/.test(socialNewPassword) || !/[^A-Za-z0-9]/.test(socialNewPassword)) {
      setError('Choose a password with uppercase, lowercase, number, and symbol characters.');
      return;
    }

    setError('');
    setSuccess('');
    setSettingPassword(true);

    try {
      const result = await setAccountPassword(socialNewPassword);
      if (result?.success) {
        setSuccess('Password set successfully. You can now sign in with email and password too.');
        setSocialNewPassword('');
        setSocialConfirmPassword('');
      } else {
        setError(result?.error || 'Unable to set password.');
      }
    } catch (passwordError) {
      setError(typeof passwordError === 'string' ? passwordError : passwordError?.message || 'Unable to set password.');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setError('');
    setSuccess('');
    setVerifyingEmail(true);
    try {
      const result = await resendEmailVerification(email);
      if (result?.success) {
        setSuccess('Verification email sent. Please check your inbox.');
      } else {
        setError(result?.error || 'Unable to resend verification email.');
      }
    } catch (verifyError) {
      setError(typeof verifyError === 'string' ? verifyError : verifyError?.message || 'Unable to resend verification email.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleLogoutOtherDevices = () => {
    setSuccess('');
    setError('Logging out from other devices is not yet available.');
  };

  return (
    <Layout>
      <div className="scan-results-container">
        <header className="results-header">
          <div className="header-content">
            <div className="results-header-title">
              <p className="scan-breadcrumb">Home &gt; Operator</p>
              <h1>Profile</h1>
              <p className="scan-last-updated">Identity, Settings & Scan History.</p>
            </div>
            <div className="header-actions">
              <BackButton to="/" label="RETURN TO BASE" />
            </div>
          </div>
        </header>

        <div className="scan-results-workspace">
        {error ? <div className="profile-feedback profile-feedback--error">{error}</div> : null}
        {success ? <div className="profile-feedback profile-feedback--success">{success}</div> : null}

        <section className="profile-command">
          <div className="profile-command__stack">
            <article className="profile-command-card profile-command-card--profile">
              <div className="profile-command-card__body">
                <div className="profile-identity-content">
                  <div className="identity-card">
                    <div className="identity-details">
                      <h2>{name || 'Unnamed Operator'}</h2>
                      <p>{email || 'No email available'}</p>
                      <div className="identity-tags">
                        <span className="identity-tag identity-tag--cyan">Member Since {formatDate(user?.created_at)}</span>
                        <span className="identity-tag identity-tag--green">{emailVerified ? 'Email Verified' : 'Verification Pending'}</span>
                        <span className="identity-tag identity-tag--yellow">Account Age {accountAgeDays} days</span>
                      </div>
                    </div>
                  </div>

                  <form className="identity-form" onSubmit={handleProfileUpdate}>
                    <div className="profile-form__cluster">
                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="fullName">Display Name</label>
                        <input
                          id="fullName"
                          className="profile-form__input"
                          type="text"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          required
                        />
                      </div>
                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="profileEmail">Email</label>
                        <input id="profileEmail" className="profile-form__input" type="email" value={email} disabled />
                        <span className="profile-form__hint">Email is managed by the identity service.</span>
                      </div>
                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="username">Username</label>
                        <input id="username" className="profile-form__input" type="text" value={user?.username || 'N/A'} disabled />
                      </div>
                    </div>
                    <button type="submit" className="profile-button profile-button--primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Identity'}
                    </button>
                  </form>
                </div>
              </div>
            </article>

            <article className="profile-command-card">
              <div className="profile-command-card__label">Scan Activity // Last 12 Months</div>
              <div className="profile-command-card__body">
                {loadingScans ? (
                  <div className="profile-muted" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center', padding: '2rem' }}>
                    <PixelSpinner size={32} /> Loading scan activity...
                  </div>
                ) : (
                  <div className="scan-heatmap">
                    <div className="scan-heatmap__stats">
                      <div className="scan-heatmap__stat">
                        <span>Total Scans</span>
                        <strong>{heatmap.totalScans}</strong>
                      </div>
                      <div className="scan-heatmap__stat">
                        <span>Active Days</span>
                        <strong>{heatmap.activeDays}</strong>
                      </div>
                      <div className="scan-heatmap__stat">
                        <span>Peak / Day</span>
                        <strong>{heatmap.maxCount}</strong>
                      </div>
                    </div>
                    <div className="scan-heatmap__calendar">
                      <div className="scan-heatmap__canvas">
                        <div className="scan-heatmap__months">
                          {heatmap.monthLabels.map((label, index) => (
                            <span key={`month-${index}`}>{label}</span>
                          ))}
                        </div>
                        <div className="scan-heatmap__grid">
                          <div className="scan-heatmap__days">
                            <span>Mon</span>
                            <span></span>
                            <span>Wed</span>
                            <span></span>
                            <span>Fri</span>
                            <span></span>
                            <span></span>
                          </div>
                          <div className="scan-heatmap__weeks">
                            {heatmap.weeks.map((week, weekIndex) => (
                              <div key={`week-${weekIndex}`} className="scan-heatmap__week">
                                {week.map((day, dayIndex) => (
                                  <button
                                    key={`${weekIndex}-${dayIndex}`}
                                    type="button"
                                    className={`heatmap-cell ${day.inRange ? getIntensityClass(day.count) : 'heatmap-cell--empty'} ${selectedScanDate === toDateKey(day.date) ? 'heatmap-cell--selected' : ''}`}
                                    title={
                                      day.inRange
                                        ? `${day.date.toLocaleDateString()}: ${day.count} scan${day.count === 1 ? '' : 's'}`
                                        : ''
                                    }
                                    aria-label={
                                      day.inRange
                                        ? `${day.date.toLocaleDateString()}: ${day.count} scan${day.count === 1 ? '' : 's'}`
                                        : undefined
                                    }
                                    aria-pressed={day.inRange ? selectedScanDate === toDateKey(day.date) : undefined}
                                    disabled={!day.inRange || day.count === 0}
                                    onClick={() => {
                                      setSelectedScanDate(toDateKey(day.date));
                                      setActiveTab('activity');
                                    }}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="scan-heatmap__legend">
                        <span>Less</span>
                        <div className="heatmap-cell heatmap-cell--0" />
                        <div className="heatmap-cell heatmap-cell--1" />
                        <div className="heatmap-cell heatmap-cell--2" />
                        <div className="heatmap-cell heatmap-cell--3" />
                        <div className="heatmap-cell heatmap-cell--4" />
                        <span>More</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </article>
          </div>
        </section>
        
        <div className="scan-tabs-divider" aria-hidden="true"></div>
        <div className="tabs-shell">
          <TabbedInterface
            tabs={[
              { id: 'overview', label: 'Overview', className: `tab tab-cyan ${activeTab === 'overview' ? 'active' : ''}` },
              { id: 'security', label: 'Security & Settings', className: `tab tab-coral ${activeTab === 'security' ? 'active' : ''}` },
              { id: 'activity', label: 'Security Activity', className: `tab tab-yellow ${activeTab === 'activity' ? 'active' : ''}` }
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            renderPanel={false}
            className="tabs-container"
          />
        </div>

        <div className="tabs-content">
        {activeTab === 'overview' ? (
          <section className="results-section detailed-outline">
            <article className="profile-panel">
              <div className="profile-panel__header profile-panel__header--cyan">
                <h3>Account Snapshot</h3>
              </div>
              <div className="profile-panel__body">
                <div className="snapshot-list">
                  <div className="snapshot-item">
                    <span>Last Sign-In</span>
                    <strong>{formatDateTime(user?.last_login)}</strong>
                  </div>
                  <div className="snapshot-item">
                    <span>Account Status</span>
                    <strong>{user?.is_active === false ? 'Inactive' : 'Active'}</strong>
                  </div>
                  <div className="snapshot-item">
                    <span>Email Status</span>
                    <strong>{emailVerified ? 'Verified' : 'Pending'}</strong>
                  </div>
                </div>
              </div>
            </article>
          </section>
        ) : null}

        {activeTab === 'security' ? (
          <section className="results-section detailed-outline">
            {passwordEnabled ? (
              <article className="profile-panel">
                <div className="profile-panel__header profile-panel__header--coral">
                  <h3>Password</h3>
                </div>
                <div className="profile-panel__body">
                  <form onSubmit={handlePasswordChange}>
                    <div className="profile-form__cluster">
                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="currentPassword">Current Password</label>
                        <div className="profile-form__input-wrapper">
                          <input
                            id="currentPassword"
                            className="profile-form__input"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            required
                          />
                          <button
                            className="profile-form__toggle"
                            type="button"
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                            aria-pressed={showCurrentPassword}
                            onClick={() => setShowCurrentPassword((v) => !v)}
                          >
                            {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="newPassword">New Password</label>
                        <div className="profile-form__input-wrapper">
                          <input
                            id="newPassword"
                            className="profile-form__input"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            required
                          />
                          <button
                            className="profile-form__toggle"
                            type="button"
                            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                            aria-pressed={showNewPassword}
                            onClick={() => setShowNewPassword((v) => !v)}
                          >
                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="confirmPassword">Confirm Password</label>
                        <input
                          id="confirmPassword"
                          className="profile-form__input"
                          type="password"
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <button type="submit" className="profile-button profile-button--primary" disabled={loading}>
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              </article>
            ) : (
              <article className="profile-panel">
                <div className="profile-panel__header profile-panel__header--green">
                  <h3>Set Password for Social Sign-In</h3>
                </div>
                <div className="profile-panel__body">
                  <p className="profile-muted">
                    Use this if you signed up with Google or GitHub and want to add email/password access to the same account.
                  </p>
                  <form onSubmit={handleSetPassword}>
                    <div className="profile-form__cluster">
                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="socialNewPassword">New Password</label>
                        <div className="profile-form__input-wrapper">
                          <input
                            id="socialNewPassword"
                            className="profile-form__input"
                            type={showSocialPassword ? 'text' : 'password'}
                            value={socialNewPassword}
                            onChange={(event) => setSocialNewPassword(event.target.value)}
                            required
                          />
                          <button
                            className="profile-form__toggle"
                            type="button"
                            aria-label={showSocialPassword ? 'Hide new password' : 'Show new password'}
                            aria-pressed={showSocialPassword}
                            onClick={() => setShowSocialPassword((v) => !v)}
                          >
                            {showSocialPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="profile-form__group">
                        <label className="profile-form__label" htmlFor="socialConfirmPassword">Confirm Password</label>
                        <div className="profile-form__input-wrapper">
                          <input
                            id="socialConfirmPassword"
                            className="profile-form__input"
                            type={showSocialConfirmPassword ? 'text' : 'password'}
                            value={socialConfirmPassword}
                            onChange={(event) => setSocialConfirmPassword(event.target.value)}
                            required
                          />
                          <button
                            className="profile-form__toggle"
                            type="button"
                            aria-label={showSocialConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                            aria-pressed={showSocialConfirmPassword}
                            onClick={() => setShowSocialConfirmPassword((v) => !v)}
                          >
                            {showSocialConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <button type="submit" className="profile-button profile-button--primary" disabled={settingPassword}>
                      {settingPassword ? 'Setting Password...' : 'Set Password'}
                    </button>
                  </form>
                </div>
              </article>
            )}

            <article className="profile-panel">
              <div className="profile-panel__header profile-panel__header--yellow">
                <h3>Session Security</h3>
              </div>
              <div className="profile-panel__body">
                <div className="snapshot-list">
                  <div className="snapshot-item">
                    <span>Active Sessions</span>
                    <strong>1 (current device)</strong>
                  </div>
                  <div className="snapshot-item">
                    <span>Current Session Started</span>
                    <strong>{formatDateTime(user?.last_login)}</strong>
                  </div>
                </div>
                <div className="profile-panel__actions">
                  <button type="button" className="profile-button profile-button--small" onClick={handleLogoutOtherDevices}>
                    Logout All Other Devices
                  </button>
                </div>
              </div>
            </article>

            <article className="profile-panel">
              <div className="profile-panel__header profile-panel__header--pink">
                <h3>Danger Zone</h3>
              </div>
              <div className="profile-panel__body">
                <div className="profile-panel__actions">
                  <button
                    type="button"
                    className="profile-button profile-button--small"
                    onClick={handleResendVerification}
                    disabled={verifyingEmail || !email || emailVerified}
                  >
                    {verifyingEmail ? 'Sending...' : 'Resend Verification'}
                  </button>
                  <button type="button" className="profile-button profile-button--danger" disabled>
                    Request Account Deletion
                  </button>
                </div>
              </div>
            </article>
          </section>
        ) : null}

        {activeTab === 'activity' ? (
          <section className="results-section detailed-outline">
            <article className="profile-panel">
              <div className="profile-panel__header profile-panel__header--yellow">
                <h3>Recent Security Activity</h3>
              </div>
              <div className="profile-panel__body">
                <div className="snapshot-list">
                  <div className="snapshot-item">
                    <span>Failed Auth Attempts</span>
                    <strong>{failedAuthAttempts}</strong>
                  </div>
                  <div className="snapshot-item">
                    <span>Last Password Change</span>
                    <strong>{formatDateTime(lastPasswordChange)}</strong>
                  </div>
                </div>

                <h4 className="activity-subtitle">Last Scans Run</h4>
                {selectedScanDate ? (
                  <div className="profile-panel__actions">
                    <p className="profile-muted">
                      Showing scans for <strong>{toDisplayDate(selectedScanDate)}</strong>
                    </p>
                    <button
                      type="button"
                      className="profile-button profile-button--small profile-button--compact"
                      onClick={() => setSelectedScanDate('')}
                    >
                      Clear Day Filter
                    </button>
                  </div>
                ) : null}
                {loadingScans ? (
                  <div className="profile-muted" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center', padding: '2rem' }}>
                    <PixelSpinner size={32} /> Loading recent scans...
                  </div>
                ) : (selectedScanDate ? scansForSelectedDate : recentScans).length === 0 ? (
                  <p className="profile-muted">No scans found yet.</p>
                ) : (
                  <ul className="site-list">
                    {(selectedScanDate ? scansForSelectedDate : recentScans).map((scan, index) => {
                      const scanId = scan?.scan_id || scan?.id || `scan-${index}`;
                      return (
                        <li key={scanId} className="site-list__item">
                          <span>{scan?.target_url || 'Unknown target'}</span>
                          <span>{toStatusLabel(scan?.status)} // {formatDateTime(getScanTimestamp(scan))}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </article>
          </section>
        ) : null}
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default Profile;
